import { AuthResponse, SignupResponse, User, Project, Image, ImageSummary, JobResponse, BatchJobResponse, StylingHelpResponse, JobStatusResponse, RoomType, RoomTypeName, AdminUser, CreditTransaction, PaymentHistoryItem, VideoResponse, AdminImageSummary, AdminImageDetail } from './types';

const getApiBaseUrl = () => {
  const hostname = window.location.hostname;
  
  // Production domains - use relative /api path (same-origin, no CORS needed)
  if (hostname === 'estylingstudio.se' || hostname === 'www.estylingstudio.se' ||
      hostname === 'estylingstudio.com' || hostname === 'www.estylingstudio.com') {
    return '/api';
  }

  // Dev environment - same domain for frontend and backend
  if (hostname === 'estyling-dev.bottar.ai') {
    return '/api';
  }
  
  // Local development with proxy
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '/api';
  }
  
  // Lovable preview and other environments - use dev API
  return 'https://dev.estylingstudio.com/api';
};

export const API_BASE_URL = getApiBaseUrl();

class ApiClient {
  private token: string | null = null;

  constructor() {
    // Load token from localStorage on init
    this.token = localStorage.getItem('auth_token');
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  getToken() {
    return this.token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const url = `${API_BASE_URL}${endpoint}`;
    console.log('API Request:', { url, method: options.method || 'GET', body: options.body });

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        mode: 'cors',
      });

      console.log('API Response:', { status: response.status, ok: response.ok });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Request failed' }));
        console.error('API Error:', error);
        // FastAPI lägger felet i `detail` (sträng eller valideringslista) - inte `message`
        const detailMsg = typeof error.detail === 'string'
          ? error.detail
          : Array.isArray(error.detail) ? error.detail[0]?.msg : undefined;
        const apiError: any = new Error(error.message || detailMsg || `HTTP error ${response.status}`);
        apiError.response = { status: response.status };
        throw apiError;
      }

      const data = await response.json();
      console.log('API Success:', data);
      return data;
    } catch (error) {
      console.error('API Fetch Error:', error);
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Kunde inte nå servern. Kontrollera din internetanslutning eller kontakta supporten om problemet kvarstår.');
      }
      throw error;
    }
  }

  // Auth endpoints
  async resendConfirmation(email: string): Promise<SignupResponse> {
    return this.request<SignupResponse>('/auth/resend-confirmation', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async signup(email: string, name: string, password: string, lang?: string): Promise<SignupResponse> {
    const response = await this.request<SignupResponse>('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, name, password, lang }),
    });
    return response;
  }

  async login(email: string, password: string): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setToken(response.access_token);
    return response;
  }

  async getMe(): Promise<User> {
    return this.request<User>('/me');
  }

  logout() {
    this.setToken(null);
  }

  async confirmEmail(token: string): Promise<void> {
    await this.request('/auth/confirm-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    });
  }

  // Project endpoints
  async createProject(name: string, description: string): Promise<Project> {
    return this.request<Project>('/projects', {
      method: 'POST',
      body: JSON.stringify({ name, description }),
    });
  }

  async getProjects(): Promise<Project[]> {
    return this.request<Project[]>('/projects');
  }

  async getProject(projectId: string): Promise<Project> {
    return this.request<Project>(`/projects/${projectId}`);
  }

  async updateProject(projectId: string, data: { name?: string; description?: string }): Promise<Project> {
    return this.request<Project>(`/projects/${projectId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Image endpoints
  async uploadImage(data: {
    file: File;
    project_id?: string;
    name?: string;
    room_type: string; // Now required
  }): Promise<import('./types').UploadImageResponse> {
    const formData = new FormData();
    formData.append('file', data.file);

    // Ensure we always send the backend key (e.g. living-room), not a localized label
    const normalizedRoomType = data.room_type.replace(/_/g, '-');
    formData.append('room_type', normalizedRoomType); // Required field

    if (data.project_id) formData.append('project_id', data.project_id);
    if (data.name) formData.append('name', data.name);

    const headers: HeadersInit = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const url = `${API_BASE_URL}/images/upload`;
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
      mode: 'cors',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      const apiError: any = new Error(error.message || `HTTP error ${response.status}`);
      apiError.response = { status: response.status };
      throw apiError;
    }

    const responseData = await response.json();
    return responseData; // Return full response with jobId, bottarJobId, etc.
  }

  async uploadImages(data: {
    files: { file: File; room_type: string; name?: string }[];
    project_id?: string;
  }): Promise<{ results: import('./types').UploadImageResponse[] }> {
    const headers: HeadersInit = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const formData = new FormData();
    
    if (data.project_id) {
      formData.append('project_id', data.project_id);
    }
    
    // Append all files first, then all room_types in same order
    for (const item of data.files) {
      formData.append('files', item.file);
    }
    for (const item of data.files) {
      const normalizedRoomType = item.room_type.replace(/_/g, '-');
      formData.append('room_types', normalizedRoomType);
    }

    const url = `${API_BASE_URL}/images/upload/batch`;
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
      mode: 'cors',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      const apiError: any = new Error(error.message || `HTTP error ${response.status}`);
      apiError.response = { status: response.status };
      throw apiError;
    }

    const responseData = await response.json();
    return responseData;
  }

  async createImage(data: {
    project_id: string | null;
    name: string;
    room_type: string;
    original_url?: string;
    image_blob?: string;
  }): Promise<Image> {
    return this.request<Image>('/images', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getImages(): Promise<Image[]> {
    return this.request<Image[]>('/images');
  }

  async getProjectImagesSummary(projectId: string): Promise<ImageSummary[]> {
    return this.request<ImageSummary[]>(`/images/projects/${projectId}/images/summary`);
  }

  async getImage(imageId: string): Promise<Image> {
    return this.request<Image>(`/images/${imageId}`);
  }

  async getImageDownloadUrl(imageId: string, variant?: string): Promise<{ url: string; expiresIn: number }> {
    const queryParam = variant ? `?variant=${variant}` : '';
    return this.request<{ url: string; expiresIn: number }>(`/images/${imageId}/download-url${queryParam}`);
  }

  async updateImage(imageId: string, data: { name?: string; room_type?: string; selected?: boolean; selected_variant?: string; room_area_m2?: number }): Promise<Image> {
    return this.request<Image>(`/images/${imageId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async selectImage(imageId: string, variant: string): Promise<Image> {
    return this.request<Image>(`/images/${imageId}/select?variant=${variant}`, {
      method: 'POST',
    });
  }

  async unselectImage(imageId: string): Promise<Image> {
    return this.request<Image>(`/images/${imageId}/unselect`, {
      method: 'POST',
    });
  }

  async deleteImage(imageId: string): Promise<void> {
    await this.request<void>(`/images/${imageId}`, {
      method: 'DELETE',
    });
  }

  // Function endpoints
  async removeFurniture(imageId: string, roomType: string): Promise<JobResponse> {
    return this.request<JobResponse>('/functions/remove-furniture', {
      method: 'POST',
      body: JSON.stringify({ imageId, roomType }),
    });
  }

  async checkGenerationStatus(jobId: string): Promise<JobStatusResponse> {
    return this.request<JobStatusResponse>('/functions/check-generation-status', {
      method: 'POST',
      body: JSON.stringify({ jobId }),
    });
  }

  async getStylingHelp(
    imageId: string,
    roomType: string,
    customInput?: string,
    emptyRoomImage?: string,
    generateImage: boolean = false
  ): Promise<StylingHelpResponse> {
    return this.request<StylingHelpResponse>('/functions/styling-help', {
      method: 'POST',
      body: JSON.stringify({ 
        imageId, 
        roomType, 
        customInput, 
        emptyRoomImage, 
        generateImage 
      }),
    });
  }

  async fillRoom(imageId: string): Promise<JobResponse> {
    return this.request<JobResponse>('/functions/fill-room', {
      method: 'POST',
      body: JSON.stringify({ imageId }),
    });
  }

  async generateModel3(imageId: string): Promise<JobResponse> {
    return this.request<JobResponse>('/functions/generate-model3', {
      method: 'POST',
      body: JSON.stringify({ imageId }),
    });
  }

  async styleAndRender(imageId: string, extraPrompt?: string, styleTag?: string, bedType?: 'single' | 'double', enhanceLight?: boolean): Promise<JobResponse> {
    return this.request<JobResponse>('/functions/style-and-render', {
      method: 'POST',
      body: JSON.stringify({ imageId, extra_prompt: extraPrompt, style_tag: styleTag, bed_type: bedType, enhance_light: enhanceLight }),
    });
  }

  async styleAndRenderBatch(imageId: string, extraPrompt?: string, styleTag?: string, bedType?: 'single' | 'double', enhanceLight?: boolean): Promise<BatchJobResponse> {
    return this.request<BatchJobResponse>('/functions/style-and-render-batch', {
      method: 'POST',
      body: JSON.stringify({ imageId, extra_prompt: extraPrompt, style_tag: styleTag, bed_type: bedType, enhance_light: enhanceLight }),
    });
  }

  async generateVideo(imageId: string, variant: 'style1' | 'style2' | 'style3' | 'processed' | 'original' = 'style1'): Promise<VideoResponse> {
    return this.request<VideoResponse>('/functions/generate-video', {
      method: 'POST',
      body: JSON.stringify({ imageId, variant }),
    });
  }

  async replaceFurniture(imageId: string, extraPrompt?: string, styleTag?: string): Promise<JobResponse> {
    return this.request<JobResponse>('/functions/replace-furniture', {
      method: 'POST',
      body: JSON.stringify({ imageId, extra_prompt: extraPrompt, style_tag: styleTag }),
    });
  }

  async touchUp(imageId: string, prompt: string, variant: string): Promise<JobResponse> {
    return this.request<JobResponse>('/functions/touch-up', {
      method: 'POST',
      body: JSON.stringify({ imageId, prompt, variant }),
    });
  }

  async finalizeGeneration(jobId: string): Promise<{ success: boolean; creditsDeducted: boolean }> {
    return this.request<{ success: boolean; creditsDeducted: boolean }>('/functions/finalize-generation', {
      method: 'POST',
      body: JSON.stringify({ jobId }),
    });
  }

  async getUserJobs(status?: string): Promise<JobStatusResponse[]> {
    const queryParam = status ? `?status=${status}` : '';
    return this.request<JobStatusResponse[]>(`/functions/jobs${queryParam}`);
  }

  async getJobsByImage(imageId: string): Promise<JobStatusResponse[]> {
    return this.request<JobStatusResponse[]>(`/functions/jobs/by-image/${imageId}`);
  }

  async getCreditHistory(): Promise<CreditTransaction[]> {
    return this.request<CreditTransaction[]>('/functions/jobs/credits/history');
  }

  // Admin endpoints
  async getRoomTypes(): Promise<RoomType[]> {
    return this.request<RoomType[]>('/room-types');
  }

  async getRoomTypeNames(lang?: string): Promise<RoomTypeName[]> {
    const query = lang ? `?lang=${lang}` : '';
    return this.request<RoomTypeName[]>(`/room-types/names${query}`);
  }

  async updateRoomTypePrompts(
    roomTypeId: string, 
    data: { empty_room_prompt?: string; styling_prompt?: string }
  ): Promise<RoomType> {
    return this.request<RoomType>(`/room-types/${roomTypeId}/prompts`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getAdminJobs(): Promise<JobStatusResponse[]> {
    return this.request<JobStatusResponse[]>('/admin/jobs');
  }

  async getAdminUsers(): Promise<AdminUser[]> {
    return this.request<AdminUser[]>('/admin/users');
  }

  async addUserCredits(userId: string, amount: number): Promise<AdminUser> {
    return this.request<AdminUser>(`/admin/users/${userId}/credits`, {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });
  }

  async updateProfile(data: { name?: string; lang?: string }): Promise<User> {
    return this.request<User>('/user/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async createCheckoutSession(credits: number, amount: number): Promise<{ checkout_url: string }> {
    return this.request<{ checkout_url: string }>('/payments/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify({ credits, amount }),
    });
  }

  async createSubscriptionSession(planType: string): Promise<{ checkout_url: string }> {
    return this.request<{ checkout_url: string }>('/payments/create-subscription-session', {
      method: 'POST',
      body: JSON.stringify({ plan_type: planType }),
    });
  }

  async getPaymentHistory(): Promise<PaymentHistoryItem[]> {
    return this.request<PaymentHistoryItem[]>('/payments/history');
  }

  async getUserSubscription(): Promise<{ status: string; plan_type: string | null; current_period_end: string | null; cancel_at_period_end?: boolean }> {
    return this.request<{ status: string; plan_type: string | null; current_period_end: string | null; cancel_at_period_end?: boolean }>('/user/subscription');
  }

  async cancelSubscription(): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>('/payments/cancel-subscription', {
      method: 'DELETE',
    });
  }

  async resumeSubscription(): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>('/payments/resume-subscription', {
      method: 'POST',
    });
  }

  async changePlan(planType: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>('/payments/change-plan', {
      method: 'POST',
      body: JSON.stringify({ plan_type: planType }),
    });
  }

  // Share endpoints
  async createProjectShare(projectId: string, expiresAt?: string): Promise<{ shareUrl: string; token: string }> {
    return this.request<{ shareUrl: string; token: string }>(`/projects/${projectId}/share`, {
      method: 'POST',
      body: JSON.stringify(expiresAt ? { expiresAt } : {}),
    });
  }

  async deleteProjectShare(projectId: string): Promise<void> {
    await this.request<void>(`/projects/${projectId}/share`, {
      method: 'DELETE',
    });
  }

  async getSharedProject(token: string): Promise<{
    name: string;
    description: string;
    lang: string;
    images: Array<{
      id: string;
      name: string;
      room_type: string;
      selected_variant: string | null;
      url: string;
    }>;
  }> {
    // This endpoint doesn't require auth
    const url = `${API_BASE_URL}/share/${token}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      mode: 'cors',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `HTTP error ${response.status}`);
    }

    return response.json();
  }

  async getProjectShareStatus(projectId: string): Promise<{ hasShare: boolean; shareUrl?: string }> {
    return this.request<{ hasShare: boolean; shareUrl?: string }>(`/projects/${projectId}/share/status`);
  }

  // Admin images endpoints
  async getAdminImages(params?: { limit?: number; offset?: number; user_email?: string }): Promise<AdminImageSummary[]> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset) queryParams.set('offset', params.offset.toString());
    if (params?.user_email) queryParams.set('user_email', params.user_email);
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return this.request<AdminImageSummary[]>(`/admin/images${query}`);
  }

  async getAdminImage(imageId: string): Promise<AdminImageDetail> {
    return this.request<AdminImageDetail>(`/admin/images/${imageId}`);
  }

  async compositeImages(images: File[], prompt: string): Promise<{ image_data: string; mime_type: string }> {
    const formData = new FormData();
    for (const image of images) {
      formData.append('images', image);
    }
    formData.append('prompt', prompt);

    const headers: HeadersInit = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE_URL}/admin/composite`, {
      method: 'POST',
      headers,
      body: formData,
      mode: 'cors',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.detail || error.message || `HTTP error ${response.status}`);
    }

    return response.json();
  }
}

export const api = new ApiClient();
